import { useEffect, useRef, useState } from 'react'
import { useAppContext } from '../AppContext'
import { showNotification } from '../util/notifications'
import useUserIsCurator from '../hooks/useUserIsCurator'
import useCurrentRoom from '../hooks/useCurrentRoom'
import { useMutation, useQuery } from '@apollo/client/react'
import PlaybackFinished from '../graphql/queries/PlaybackFinished'
import RoomQueue from '../graphql/queries/RoomQueue'
import RemoveRequest from '../graphql/queries/RemoveRequest'
import ReportTrackProblem from '../graphql/queries/ReportTrackProblem'
import { ActionIcon, Loader, Transition, Tooltip } from '@mantine/core'
import TrackLinkPopover from '../ui/TrackLinkPopover'
import YouTubePlayer from './YouTubePlayer'
import { useModalStack } from '../ui/ModalStack'

const cdnUrl = process.env.REACT_APP_CDN_URL

export default function Player() {

    const { stopAudioTimestamp } = useAppContext()

    const room = useCurrentRoom()
    const userIsRoomAdmin = useUserIsCurator()
    const { pushModal } = useModalStack()

    // Play queue and now playing data
    const { data: roomQueueData, refetch: refetchQueue } = useQuery(RoomQueue, {
        fetchPolicy: 'network-only',
        returnPartialData: true,
        skip: !room,
        variables: { roomId: room?.id }
    })
    const nowPlaying = roomQueueData?.room.queue[0]
    const track = nowPlaying?.track

    // Determine which player to use for the current track
    const isYouTubeTrack = !!(track?.youtubeId)

    // The audio element and its datastore remain constant for all renders.
    const audio = useRef(new Audio()).current
    const ytPlayerRef = useRef(null)   // ref to the YouTubePlayer component handle

    // Helper: returns whichever player is active right now
    function activePlayer() {
        return isYouTubeTrack ? ytPlayerRef.current : audio
    }

    const playerData = useRef({ room, nowPlaying, ended: false }).current
    playerData.room = room
    playerData.nowPlaying = nowPlaying
    playerData.userIsRoomAdmin = userIsRoomAdmin
    // keep isYouTubeTrack accessible to callbacks that close over playerData
    playerData.isYouTubeTrack = isYouTubeTrack

    const [ playbackFinishedMutation ] = useMutation(PlaybackFinished, { refetchQueries: [ RoomQueue ] })
    const [ skipMutation, { loading: isSkipping } ] = useMutation(RemoveRequest)
    const [ reportTrackProblemMutation ] = useMutation(ReportTrackProblem)

    const [ shouldPlay, setShouldPlay ] = useState(false)
    const [ playHeadPercent, setPlayHeadPercent ] = useState(0)
    const [ isYouTubeFullscreen, setIsYouTubeFullscreen ] = useState(false)
    audio.autoplay = shouldPlay
    audio.preload = shouldPlay ? 'auto' : 'none'

    const [ volume, setVolume ] = useState(100)
    const [ showVolume, setShowVolume ] = useState(false)
    const volumeButtonRef = useRef(null)
    const volumePopoverRef = useRef(null)
    audio.volume = volume / 100

    // Track whether we've attempted initial autoplay on page load
    const hasAttemptedInitialAutoplay = useRef(false)
    const lastPositionFingerprint = useRef('')

    // Choose icon based on volume level
    const volumeIcon = volume === 0 ? 'volume_off' : (volume <= 50 ? 'volume_down' : 'volume_up')

    useEffect(() => {
        audio.onabort = () => onPlayerEvent('abort')
        audio.oncanplay = () => onPlayerEvent('canPlay')
        audio.oncanplaythrough = () => onPlayerEvent('canPlayThrough')
        audio.ondurationchange = () => onPlayerEvent('durationChange')
        audio.onemptied = () => onPlayerEvent('emptied')
        audio.onended = () => onPlayerEvent('ended')
        audio.onerror = () => onPlayerEvent('error')
        audio.onloadeddata = () => onPlayerEvent('loadedData')
        audio.onloadedmetadata = () => onPlayerEvent('loadedMetaData')
        audio.onloadstart = () => onPlayerEvent('loadStart')
        audio.onpause = () => onPlayerEvent('pause')
        audio.onplay = () => onPlayerEvent('play')
        audio.onplaying = () => onPlayerEvent('playing')
        audio.onprogress = () => onPlayerEvent('progress')
        audio.onseeked = () => onPlayerEvent('seeked')
        audio.onseeking = () => onPlayerEvent('seeking')
        audio.onstalled = () => onPlayerEvent('stalled')
        audio.onsuspend = () => onPlayerEvent('suspend')
        audio.onwaiting = () => onPlayerEvent('waiting')

        // Set up MediaSession action handlers (only once)
        if ('mediaSession' in navigator) {
            try {
                navigator.mediaSession.setActionHandler('play', () => { if (activePlayer()?.paused) togglePlay() })
                navigator.mediaSession.setActionHandler('pause', () => { if (!activePlayer()?.paused) togglePlay() })
                navigator.mediaSession.setActionHandler('stop', () => { if (!activePlayer()?.paused) togglePlay() })
                navigator.mediaSession.setActionHandler('nexttrack', () => { if (playerData.userIsRoomAdmin) skip() })
            } catch (e) {
                console.warn('MediaSession action handlers failed:', e)
            }
        }

        return () => {
            // Cleanup on unmount: ensure audio fully stops and resources released
            try {
                stopUpdates()
                clearTimeout(playerData.errorTask)
                playerData.errorTask = 0
                audio.pause()
                audio.removeAttribute('src')
                audio.load() // release
            } catch (e) {
                console.warn('Error during player unmount cleanup', e)
            }
        }
    }, [])

    // Track YouTube fullscreen state
    useEffect(() => {
        function onFullscreenChange() {
            setIsYouTubeFullscreen(!!document.fullscreenElement)
        }
        document.addEventListener('fullscreenchange', onFullscreenChange)
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
    }, [])

    function toggleFullscreen() {
        if (document.fullscreenElement) {
            document.exitFullscreen()
        } else {
            ytPlayerRef.current?.getIframe()?.requestFullscreen?.()
        }
    }

    // Close volume popover when clicking outside
    useEffect(() => {        function onDocMouseDown(e) {
            if (!showVolume) return
            const inButton = volumeButtonRef.current && volumeButtonRef.current.contains(e.target)
            const inPopover = volumePopoverRef.current && volumePopoverRef.current.contains(e.target)
            if (!inButton && !inPopover) setShowVolume(false)
        }
        document.addEventListener('mousedown', onDocMouseDown)
        return () => document.removeEventListener('mousedown', onDocMouseDown)
    }, [showVolume])

    // Listen for stopAudio action and pause playback
    useEffect(() => {
        if (stopAudioTimestamp > 0) {
            const player = activePlayer()
            if (player && !player.paused) {
                console.log('Player received stopAudio action, pausing playback')
                setShouldPlay(false)
                player.pause()
            }
        }
    }, [stopAudioTimestamp])

    useEffect(() => {
        if (playerData.errorTask) {
            clearTimeout(playerData.errorTask)
            playerData.errorTask = 0
            playerData.retryCount = 0
        }
        let taskId
        console.log(`player source change: ${nowPlaying?.id} @ ${nowPlaying?.playTime}`)
        if (nowPlaying) {
            const offset = currentOffset()
            console.log(`nowPlaying changed.  playing ${track?.title} by ${track?.artist} (youtubeId: ${track?.youtubeId ?? 'none'}) @ ${offset}`)

            // Set initial metadata immediately (for systems that check early)
            updateMediaSessionMetadata()

            if (isYouTubeTrack) {
                // Stop/clear the HTML5 audio element when switching to YouTube
                audio.pause()
                audio.src = ''

                // The YouTubePlayer component handles cuing via its own useEffect on youtubeId.
                // We start the polling task for progress tracking.
                playerData.durationSet = false
                playerData.suspended = false
                playerData.ended = false
                lastPositionFingerprint.current = ''
                taskId = startUpdates()
            } else {
                // Stop the YouTube player if it was running
                ytPlayerRef.current?.pause()

                audio.src = trackUrl(track)
                audio.currentTime = offset
                taskId = startUpdates()
                playerData.durationSet = false
                playerData.suspended = false
                playerData.ended = false
                lastPositionFingerprint.current = ''
            }

            // Attempt initial autoplay only once when page first loads with a track
            if (!hasAttemptedInitialAutoplay.current) {
                hasAttemptedInitialAutoplay.current = true
                attemptInitialAutoplay()
            }
        } else {
            console.log(`nowPlaying changed to null`)
            // Clear metadata when no track
            if ('mediaSession' in navigator) {
                navigator.mediaSession.metadata = null
                navigator.mediaSession.playbackState = 'none'
            }
            audio.src = ''
            ytPlayerRef.current?.pause()
            setPlayHeadPercent(0)
            lastPositionFingerprint.current = ''
        }
        if (taskId) {
            return () => { clearInterval(taskId) }
        }
    }, [nowPlaying?.id, nowPlaying?.playTime])

    // Enforce mutual exclusion between the two players when the track type changes.
    // This handles the case where a youtubeId is added to a track that is already
    // playing via the HTML5 audio element (same nowPlaying.id / playTime, so the
    // main nowPlaying effect does not re-run).
    useEffect(() => {
        if (isYouTubeTrack) {
            // Switched to YouTube – silence the HTML5 player immediately
            if (!audio.paused) {
                audio.pause()
            }
            audio.src = ''
        } else {
            // Switched to HTML5 – silence the YouTube player
            ytPlayerRef.current?.pause()
        }
    }, [isYouTubeTrack])

    // Sync volume to the YouTube player whenever it changes
    useEffect(() => {
        if (ytPlayerRef.current) {
            ytPlayerRef.current.volume = volume / 100
        }
    }, [volume])

    function updateMediaSessionMetadata() {
        if (!('mediaSession' in navigator) || !track) return

        try {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: track?.title ?? 'Unknown Title',
                artist: track?.artist ?? 'Unknown Artist',
                album: track?.album ?? '',
                artwork: track?.coverArt?.thumb250 ? [
                    { src: track.coverArt.thumb250, sizes: '250x250', type: 'image/jpeg' }
                ] : []
            })
            navigator.mediaSession.playbackState = shouldPlay ? 'playing' : 'paused'
            console.log('MediaSession metadata updated:', track?.title, 'by', track?.artist)
        } catch (e) {
            console.warn('Failed to set MediaSession metadata:', e)
        }
    }

    function currentOffset() {
        const defaultLatency = 30 // ms
        if (playerData.nowPlaying?.playTime) {
            const offsetPlaytime = playerData.nowPlaying?.playTime - (global.serverOffset ?? defaultLatency)
            const now = Date.now()
            const actual = (now - offsetPlaytime) / 1000
            console.log(`measured playback offset: ${actual}`)
            if (actual < 3) return 0
            else return actual
        } else {
            return 0
        }
    }

    async function attemptInitialAutoplay() {
        try {
            console.log('Attempting initial autoplay...')
            if (playerData.isYouTubeTrack) {
                const offset = currentOffset()
                if (offset > 0 && ytPlayerRef.current) {
                    ytPlayerRef.current.currentTime = offset
                }
                ytPlayerRef.current?.play()
            } else {
                audio.currentTime = currentOffset()
                await audio.play()
            }
            console.log('Initial autoplay succeeded! Setting shouldPlay to true')
            setShouldPlay(true)
            startUpdates()
        } catch (error) {
            console.log('Initial autoplay blocked by browser policy:', error.message)
            // shouldPlay remains false - user must click play button
        }
    }

    function onPlayerEvent(type) {
        console.log(`[${type}] seeking: ${audio.seeking}  readyState: ${audio.readyState}  networkState: ${audio.networkState}  duration: ${audio.duration}  currentTime: ${audio.currentTime}`)
        switch (type) {
            case 'canPlayThrough':
                playerData.durationSet = true
                if (playerData.ended && shouldPlay) {
                    playerData.ended = false
                    audio.currentTime = currentOffset()
                    audio.play().catch(error => console.error(error))
                }
                break
            case 'ended':
                playerData.ended = true
                break
            case 'error':
                if (!playerData.errorTask)
                    playerData.errorTask = setTimeout(restartPlayer, 5000)
                break
            case 'play':
                updateMediaSessionMetadata()
                if ('mediaSession' in navigator) {
                    navigator.mediaSession.playbackState = 'playing'
                }
                break
            case 'playing':
                updateMediaSessionMetadata()
                break
            case 'pause':
                if ('mediaSession' in navigator) {
                    navigator.mediaSession.playbackState = 'paused'
                }
                break
            case 'suspend':
                playerData.suspended = true
                break
            default:
        }
    }

    // Called by YouTubePlayer when the user clicks the iframe directly to play/pause.
    // nowPaused = true  → iframe was paused by the click
    // nowPaused = false → iframe was resumed by the click
    function onYouTubeIframeToggle(nowPaused) {
        if (nowPaused) {
            setShouldPlay(false)
        } else {
            // Treat as if the user clicked the play button: sync to room position
            setShouldPlay(true)
            syncPlayback()
        }
    }

    // Callbacks from YouTubePlayer component
    function onYouTubePlay() {
        updateMediaSessionMetadata()
        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = 'playing'
        }
    }

    function onYouTubePause() {
        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = 'paused'
        }
    }

    function onYouTubeEnd() {
        playerData.ended = true
        stopUpdates()
        reportPlaybackFinished()
    }

    function onYouTubeError(event) {
        const code = event?.data
        // Codes 100, 101, 150 mean the video is unavailable or blocked for embedding.
        // Report the problem automatically and advance to the next track.
        if (code === 100 || code === 101 || code === 150) {
            console.warn(`YouTubePlayer: video unavailable (error ${code}), reporting and skipping.`)
            const trackId = playerData.nowPlaying?.track?.id
            if (trackId) {
                reportTrackProblemMutation({
                    variables: {
                        trackId,
                        problemType: 'INVALID_YOUTUBE_ID',
                        description: `YouTube IFrame API error code ${code} — video unavailable or blocked for embedding.`
                    }
                }).catch(err => console.error('Failed to report track problem:', err))
            }
            reportPlaybackFinished()
            return
        }
        // For other errors, fall back to the existing retry logic.
        if (!playerData.errorTask)
            playerData.errorTask = setTimeout(restartPlayer, 5000)
    }

    function onYouTubeReady() {
        // YouTube player is ready; seek to the current room position then play if needed
        playerData.durationSet = true
        const offset = currentOffset()
        if (offset > 0) {
            ytPlayerRef.current.currentTime = offset
        }
        startUpdates()
        if (shouldPlay) {
            ytPlayerRef.current?.play()
        }
    }

    async function reportPlaybackFinished() {
        try {
            const p = playerData.nowPlaying
            console.log(`reporting track ended: ${p?.track?.title} (request ${p?.id})`)
            await playbackFinishedMutation({variables: {roomId: playerData.room?.id, requestId: p?.id}})
        } catch (error) {
            showNotification(<span className="ErrorMessage">{error.message}</span>)
        }
    }

    function restartPlayer() {
        if (!playerData.nowPlaying || !shouldPlay) return
        playerData.retryCount += 1
        if (playerData.retryCount > 3) {
            reportPlaybackFinished()
            return
        }
        const track = playerData.nowPlaying?.track
        console.log(`restarting player: error count: ${playerData.retryCount}`)
        playerData.errorTask = 0

        if (playerData.isYouTubeTrack) {
            ytPlayerRef.current?.play()
        } else {
            audio.src = ''
            audio.src = trackUrl(track)
            audio.currentTime = currentOffset()
            audio.play().catch( err => console.error(`failed to play`, err) )
        }
    }

    async function skip() {
        try {
            await skipMutation({ variables: { roomId: playerData.room.id, requestId: playerData.nowPlaying.id } })
        } catch (error) {
            console.error(`failed to skip`, error)
        }
    }

    function startUpdates() {
        stopUpdates()
        playerData.updateTask = setInterval(updatePlaybackTime, 250)
        return playerData.updateTask
    }

    function stopUpdates() {
        clearInterval(playerData.updateTask)
    }

    async function syncPlayback() {
        try {
            if (playerData.isYouTubeTrack) {
                const offset = currentOffset()
                if (offset > 0) {
                    ytPlayerRef.current.currentTime = offset
                }
                ytPlayerRef.current?.play()
            } else {
                audio.currentTime = currentOffset()
                await audio.play()
            }
            await refetchQueue()
            startUpdates()
        } catch (error) {
            console.error(error)
        }
    }

    function togglePlay() {
        const player = activePlayer()
        if (player?.paused) {
            setShouldPlay(true)
            syncPlayback()
        } else {
            setShouldPlay(false)
            player?.pause()
        }
    }

    function trackUrl(track) {
        if (!track) return ''
        const hq = track.files?.find( file => file.key.endsWith('hq.m4a') )
        const lq = track.files?.find( file => file.key.endsWith('lq.m4a') )
        const chosen = hq || lq
        return chosen ? `${cdnUrl}${chosen.key}` : ''
    }

    function updatePlaybackTime() {
        if (playerData.isYouTubeTrack) {
            const yt = ytPlayerRef.current
            if (!yt) return
            const duration = yt.duration
            const position = yt.currentTime
            const durationReady = Number.isFinite(duration) && duration > 0
            setPlayHeadPercent(durationReady ? Math.min(position, duration) / duration : 0)

            if ('mediaSession' in navigator && durationReady) {
                const fingerprint = `${duration.toFixed(2)}|${position.toFixed(2)}|1`
                if (fingerprint !== lastPositionFingerprint.current) {
                    lastPositionFingerprint.current = fingerprint
                    try {
                        navigator.mediaSession.setPositionState({ duration, position, playbackRate: 1 })
                    } catch (e) {
                        console.warn('setPositionState failed:', e)
                    }
                }
            }
            // Polling-based end detection: handles the case where the page loads
            // with the track already past its duration (the YouTube IFrame API
            // will never fire an ENDED event if the video was never played).
            if (durationReady && position >= duration) {
                stopUpdates()
                reportPlaybackFinished()
            }
        } else {
            const duration = (playerData.durationSet && Number.isFinite(audio.duration)) ? audio.duration : 0
            const position = (playerData.durationSet && Number.isFinite(audio.duration)) ? Math.min(audio.currentTime, duration) : 0
            setPlayHeadPercent(playerData.durationSet ? position / duration : 0)

            if ('mediaSession' in navigator && playerData.durationSet && duration > 0) {
                const fingerprint = `${duration.toFixed(2)}|${position.toFixed(2)}|${audio.playbackRate}`
                if (fingerprint !== lastPositionFingerprint.current) {
                    lastPositionFingerprint.current = fingerprint
                    try {
                        navigator.mediaSession.setPositionState({
                            duration,
                            position,
                            playbackRate: audio.playbackRate
                        })
                    } catch (e) {
                        console.warn('setPositionState failed:', e)
                    }
                }
            }

            if (playerData.durationSet && audio.currentTime >= audio.duration) {
                stopUpdates()
                reportPlaybackFinished()
            }
        }
    }

    let skipButton
    if (userIsRoomAdmin)
        skipButton = (
            <Tooltip label="Skip to next track" openDelay={1000} zIndex={2100}>
                <ActionIcon
                    data-tour="skip-button"
                    variant="filled"
                    color="indigo"
                    size="lg"
                    onClick={skip}
                    aria-label="Skip track"
                    style={{ marginLeft: 8 }}
                    disabled={isSkipping}
                >
                    {isSkipping ? (
                        <Loader color="white" size="xs" />
                    ) : (
                        <span className="material-symbols-outlined">skip_next</span>
                    )}
                </ActionIcon>
            </Tooltip>
        )

    return (
        <div className="Player">
            <div className="PlayerGrid" style={{ gridTemplateColumns: '1fr auto 200px', gridTemplateRows: 'auto 1fr auto' }}>
                {room && (<>
                {(track?.coverArt?.thumb250 || (isYouTubeTrack && track?.youtubeId)) && (
                    <div
                        className="PlayerArtBg"
                        style={{
                            backgroundImage: `url(${track.coverArt?.thumb250 ?? `https://img.youtube.com/vi/${track.youtubeId}/hqdefault.jpg`})`,
                            ...(!track.coverArt?.thumb250 && { backgroundSize: '200%' })
                        }}
                    />
                )}
                <div className="PlayerTrackInfo" style={{ gridRow: 2, gridColumn: 2 }}>
                    <div className="TrackTitle" data-tour="track-name">
                        <TrackLinkPopover track={track} roomId={room?.id} />
                    </div>
                    <div className="TrackArtist">{ track?.artist }</div>
                    { track?.album && <div className="TrackAlbum">{ track.album }</div> }
                    { isYouTubeTrack && track?.youtubeId && (
                        <div className="TrackYouTubeLink">
                            <a
                                href={`https://www.youtube.com/watch?v=${track.youtubeId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontFamily: 'sans-serif', fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}
                            >
                                Listen on YouTube
                            </a>
                        </div>
                    )}
                </div>
                <div className="PlayerControls" style={{ display: 'flex', alignItems: 'center', gap: 10, gridRow: 1, gridColumn: 2, justifySelf: 'end' }}>
                    <Tooltip label={shouldPlay ? "Stop listening" : "Start listening"} openDelay={1000} zIndex={2100}>
                        <ActionIcon
                            variant="filled"
                            color="indigo"
                            size="lg"
                            onClick={togglePlay}
                            aria-label={ shouldPlay ? 'Stop' : 'Play' }
                            data-tour="play-button"
                        >
                            <span className="material-symbols-outlined">{ shouldPlay ? 'stop_circle' : 'play_circle' }</span>
                        </ActionIcon>
                    </Tooltip>
                    {/* Volume button with animated popover */}
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <Tooltip label="Adjust volume" openDelay={1000} zIndex={2100}>
                            <ActionIcon
                                ref={volumeButtonRef}
                                variant="filled"
                                color="indigo"
                                size="lg"
                                onClick={() => setShowVolume(v => !v)}
                                aria-label="Adjust volume"
                            >
                                <span className="material-symbols-outlined">{ volumeIcon }</span>
                            </ActionIcon>
                        </Tooltip>
                        {/* Positioning container above the button */}
                        <div style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', zIndex: 3000 }}>
                            <Transition mounted={showVolume} transition="pop" duration={140} timingFunction="ease-out">
                                {(styles) => (
                                    <div
                                        ref={volumePopoverRef}
                                        className="PlayerVolumePopover"
                                        style={{
                                            ...styles,
                                            padding: 8,
                                            background: 'rgba(0,0,0,0.9)',
                                            border: '1px solid #444',
                                            borderRadius: 8,
                                            width: 36,
                                            height: 160,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <div
                                            style={{
                                                transform: 'rotate(-90deg)',
                                                width: 140,
                                                height: 36,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <input
                                                type="range"
                                                min={0}
                                                max={100}
                                                value={ volume }
                                                onChange={(e) => setVolume(Number(e.target.value))}
                                                style={{
                                                    appearance: 'none',
                                                    WebkitAppearance: 'none',
                                                    width: 140,
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </Transition>
                        </div>
                    </div>
                    { skipButton }
                </div>
                { track && (
                    <div style={{ gridRow: 2, gridColumn: 2, justifySelf: 'end', display: 'flex', alignItems: 'flex-start', paddingTop: 4 }}>
                        <Tooltip label="Report a problem with this track" openDelay={800} zIndex={2100}>
                            <ActionIcon
                                variant="subtle"
                                color="red"
                                size="sm"
                                onClick={() => pushModal('reportTrackProblem', { trackId: track.id, trackTitle: [track.title, track.artist].filter(Boolean).join(' — ') })}
                                aria-label="Report a problem"
                                style={{ opacity: 0.55, marginRight: 8 }}
                                styles={{ root: { ':hover': { opacity: 1 } } }}
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>flag</span>
                            </ActionIcon>
                        </Tooltip>
                        { isYouTubeTrack && (
                            <Tooltip label={isYouTubeFullscreen ? 'Exit fullscreen' : 'Fullscreen'} openDelay={800} zIndex={2100}>
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    size="sm"
                                    onClick={toggleFullscreen}
                                    aria-label={isYouTubeFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                                    style={{ opacity: 0.55, marginRight: 8 }}
                                    styles={{ root: { ':hover': { opacity: 1 } } }}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                                        {isYouTubeFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                                    </span>
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </div>
                )}
                <div style={{ gridRow: '1 / 3', gridColumn: 3 }}>
                    {isYouTubeTrack ? (
                        <YouTubePlayer
                            ref={ytPlayerRef}
                            youtubeId={track.youtubeId}
                            shouldPlay={shouldPlay}
                            volume={volume / 100}
                            onReady={onYouTubeReady}
                            onPlay={onYouTubePlay}
                            onPause={onYouTubePause}
                            onEnd={onYouTubeEnd}
                            onError={onYouTubeError}
                            onTimeUpdate={updatePlaybackTime}
                            onIframeToggle={onYouTubeIframeToggle}
                        />
                    ) : (
                        <div style={{
                            width: 200,
                            height: 200,
                            borderRadius: 4,
                            background: '#ffffff06',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            {track?.coverArt?.thumb250 ? (
                                <img
                                    src={track.coverArt.thumb250}
                                    alt={track.title ?? 'Album art'}
                                    style={{ width: 200, height: 200, objectFit: 'cover' }}
                                />
                            ) : (
                                <img
                                    src="/tunistry-note-icon-white.svg"
                                    alt="Tunistry logo"
                                    style={{
                                        width: 100,
                                        height: 100,
                                        opacity: 0.2
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div className="PlayerProgress" style={{ gridRow: 3, gridColumnEnd: 4 }}>
                    <div className="PlayerProgressBar" id="progressBar" style={{ width: `${playHeadPercent * 100}%`}}/>
                </div>
                </>)}
            </div>
        </div>
    )
}
